// Quotations (Stage 2E-2): list (Open by default), editor for drafts, details for issued quotations.
// Numbers, totals and every rule come from the database; issued quotations are never edited or deleted.
import { useState, useMemo } from "react";
import { DataTable, Select, Button, StatusBadge, Notice } from "../ui/kit/components";
import { text } from "../ui/kit/tokens";
import { loadQuotationLines, errText } from "./api";
import { npr, dualDate, ktmToday, fyOf } from "./format.js";
import { qStatus, STATUS, OPEN, validityPassed } from "./quotationDocs";
import QuotationEditor from "./QuotationEditor";
import QuotationDetails from "./QuotationDetails";

export default function Quotations({ data, reload, onOpenInvoice }) {
    const [filter, setFilter] = useState("open");
    const [editing, setEditing] = useState(null);           // null | initial draft for the editor
    const [openId, setOpenId] = useState(null);
    const [storeWarning, setStoreWarning] = useState("");
    const [err, setErr] = useState("");
    const today = ktmToday();
    const clientName = useMemo(() => Object.fromEntries(data.clients.map(c => [c.id, c.name])), [data.clients]);
    const rows = useMemo(() => data.quotations.filter(q => filter === "all" || (filter === "open") === OPEN.includes(qStatus(q))), [data.quotations, filter]);
    const fy = fyOf(today, data.calendar);
    const next = data.series.find(s => s.doc_type === "QT" && s.fy_label === fy);
    const nextQt = next ? `${data.settings?.quote_prefix || "QT"}-${fy}-${String(next.next_no).padStart(4, "0")}` : null;
    const open = data.quotations.find(q => q.id === openId) || null;

    const edit = async (q) => {
        setErr("");
        try { setEditing({ ...q, lines: await loadQuotationLines(q.id) }); } catch (e) { setErr(errText(e)); }
    };
    const duplicate = async (q) => {
        setErr("");
        try {
            const lines = await loadQuotationLines(q.id);
            setOpenId(null);
            setEditing({ client_id: q.client_id, subject: q.subject, terms: q.terms ?? "", notes: q.notes, doc_discount_type: q.doc_discount_type,
                doc_discount_value: q.doc_discount_value, lines, duplicateOf: q.doc_no || "a draft" });
        } catch (e) { setErr(errText(e)); }
    };

    if (editing) return (
        <QuotationEditor key={editing.id || "new"} initial={editing} data={{ ...data, nextQt }}
            onClose={async (changed) => { setEditing(null); if (changed) await reload(); }}
            onSaved={() => reload()}
            onIssued={async (q, warn) => { setEditing(null); setStoreWarning(warn); await reload(); setOpenId(q.id); }} />
    );
    return (
        <div>
            {err && <div style={{ marginBottom: 12 }}><Notice tone="danger">{err}</Notice></div>}
            {!fy && <div style={{ marginBottom: 12 }}><Notice tone="warning">Today's Nepali calendar month isn't verified, so quotations can't be issued yet (drafts can be saved).</Notice></div>}
            <DataTable rows={rows} defaultSort={{ key: "created", dir: "desc" }}
                searchText={q => [q.doc_no, clientName[q.client_id], q.subject].filter(Boolean).join(" ")}
                searchPlaceholder="Search number, client, subject…"
                empty={filter === "open" ? "No open quotations." : "No quotations here."}
                filters={<Select value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 140 }} aria-label="Quotation filter">
                    <option value="open">Open</option><option value="closed">Closed</option><option value="all">All</option></Select>}
                toolbarRight={<Button onClick={() => setEditing({})}>New quotation</Button>}
                onRowClick={q => (q.status === "draft" ? edit(q) : (setStoreWarning(""), setOpenId(q.id)))}
                columns={[
                    { key: "number", header: "Quotation", sortValue: q => q.doc_no || "~",
                        render: q => <div><div style={{ fontWeight: 600 }}>{q.doc_no || "Draft"}</div><div style={text.caption}>{clientName[q.client_id] || "—"}{q.subject ? ` · ${q.subject}` : ""}</div></div> },
                    { key: "created", header: "Date", sortValue: q => `${q.issue_date}${q.created_at}`, render: q => dualDate(q.issue_date, data.calendar), hideOnMobile: true },
                    { key: "valid", header: "Valid until", sortValue: q => q.valid_until, render: q => dualDate(q.valid_until, data.calendar), hideOnMobile: true },
                    { key: "total", header: "Total (NPR)", sortValue: q => Number(q.total), render: q => <span style={text.money}>{npr(q.total)}</span> },
                    { key: "status", header: "Status", sortValue: q => qStatus(q), render: q => (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <StatusBadge status={STATUS[qStatus(q)][1]} label={STATUS[qStatus(q)][0]} />
                            {validityPassed(q, today) && <StatusBadge status="overdue" label="Validity passed" />}
                        </div>) },
                ]} />
            {open && <QuotationDetails q={open} data={data} storeWarning={storeWarning} onClose={() => setOpenId(null)}
                onChanged={reload} onDuplicate={duplicate} onOpenInvoice={onOpenInvoice ? (id) => { setOpenId(null); onOpenInvoice(id); } : undefined} />}
        </div>
    );
}
